// "My Day" — today's + upcoming assigned tickets for the logged-in tech.
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Box, Card, CardContent, Chip, CircularProgress, IconButton, Typography,
} from '@mui/material';
import {
  ChevronRight as ChevronIcon,
  Directions as DirectionsIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { ticketsApi } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import {
  ReturnTripChip, StatusChip, fmtSched, mapsHref, techPriorityColors, ticketAddress,
} from './TechShared';

const ACTIVE_STATUSES = ['en_route', 'on_site', 'in_progress', 'pending_parts'];
const CLOSED_STATUSES = ['completed', 'cancelled', 'denied'];

function TicketCardMobile({ ticket, onOpen }: { ticket: any; onOpen: () => void }) {
  const pr = techPriorityColors[ticket.priority] || techPriorityColors.medium;
  const addr = ticketAddress(ticket);
  return (
    <Card
      onClick={onOpen}
      sx={{
        mb: 1.5, cursor: 'pointer', border: '1px solid #e2e8f0', borderRadius: 3,
        '&:active': { bgcolor: '#f8fafc' },
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <ScheduleIcon sx={{ fontSize: 18, color: '#14548C' }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#14548C' }}>
              {fmtSched(ticket.scheduled_start)}
            </Typography>
          </Box>
          <Chip
            label={(ticket.priority || 'medium').toUpperCase()} size="small"
            sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: pr.bg, color: pr.color }}
          />
        </Box>
        <Typography variant="body1" sx={{ fontWeight: 600, lineHeight: 1.3, mb: 0.5 }}>
          {ticket.title}
        </Typography>
        {ticket.customer_name && (
          <Typography variant="body2" color="text.secondary">{ticket.customer_name}</Typography>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
            <StatusChip status={ticket.status} />
            <ReturnTripChip ticket={ticket} />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton
              component="a" href={mapsHref(ticket)} target="_blank" rel="noopener"
              onClick={(e) => e.stopPropagation()}
              sx={{ color: '#14548C', width: 44, height: 44 }} aria-label="Directions"
            >
              <DirectionsIcon />
            </IconButton>
            <ChevronIcon sx={{ color: 'text.disabled' }} />
          </Box>
        </Box>
        {addr && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {addr}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function Section({ title, tickets, onOpen }: { title: string; tickets: any[]; onOpen: (t: any) => void }) {
  if (tickets.length === 0) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="overline" sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 1 }}>
        {title} ({tickets.length})
      </Typography>
      {tickets.map((t) => <TicketCardMobile key={t.id} ticket={t} onOpen={() => onOpen(t)} />)}
    </Box>
  );
}

export default function TechMyDayPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true); setError(null);
    try {
      const res = await ticketsApi.list({ assigned_to_id: user.id, page_size: 100 });
      const items = res.data?.items ?? res.data ?? [];
      setTickets(Array.isArray(items) ? items : []);
    } catch {
      setError('Could not load your tickets. Check your connection and pull refresh.');
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => load();
    window.addEventListener('tech-refresh', h);
    return () => window.removeEventListener('tech-refresh', h);
  }, [load]);

  const now = new Date();
  const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);
  const isToday = (iso?: string | null) => !!iso && new Date(iso).toDateString() === now.toDateString();

  const bySched = (a: any, b: any) =>
    new Date(a.scheduled_start || '2099-01-01').getTime() - new Date(b.scheduled_start || '2099-01-01').getTime();

  const active = tickets.filter((t) => ACTIVE_STATUSES.includes(t.status)).sort(bySched);
  const open = tickets.filter((t) => !ACTIVE_STATUSES.includes(t.status) && !CLOSED_STATUSES.includes(t.status));
  const today = open.filter((t) => isToday(t.scheduled_start)).sort(bySched);
  const overdue = open
    .filter((t) => t.scheduled_start && new Date(t.scheduled_start) < now && !isToday(t.scheduled_start))
    .sort(bySched);
  const upcoming = open.filter((t) => t.scheduled_start && new Date(t.scheduled_start) > endOfToday).sort(bySched);
  const unscheduled = open.filter((t) => !t.scheduled_start).sort(bySched);
  const doneToday = tickets
    .filter((t) => t.status === 'completed' && (isToday(t.actual_end) || isToday(t.updated_at)))
    .sort(bySched);

  const openTicket = (t: any) => navigate(`/tech/ticket/${t.id}`);
  const greeting = (user as any)?.first_name ? `Hi ${(user as any).first_name} — ` : '';

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {greeting}{now.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' })}
      </Typography>

      {loading ? (
        <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : tickets.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 3 }}>
          No tickets assigned to you yet. Dispatch will schedule your calls here.
        </Alert>
      ) : (
        <>
          <Section title="In progress" tickets={active} onOpen={openTicket} />
          <Section title="Today" tickets={today} onOpen={openTicket} />
          <Section title="Overdue" tickets={overdue} onOpen={openTicket} />
          <Section title="Upcoming" tickets={upcoming} onOpen={openTicket} />
          <Section title="Unscheduled" tickets={unscheduled} onOpen={openTicket} />
          <Section title="Done today" tickets={doneToday} onOpen={openTicket} />
          {active.length + today.length + overdue.length + upcoming.length + unscheduled.length + doneToday.length === 0 && (
            <Alert severity="success" sx={{ borderRadius: 3 }}>All caught up — nothing on your plate.</Alert>
          )}
        </>
      )}
    </Box>
  );
}
